<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { SavedReport } from '../../../interfaces';
import type { ApiResponse } from '@Types';
import { useADialog, useAToast } from '@Atoms';
import { useActiveFilter, useSavedReports } from '../../../composables';
import { UpdateSavedReportService } from '../../../services';

const emit = defineEmits<{
  onClear: [];
  onSaveAsNew: [];
  onUpdated: [savedReport: SavedReport];
}>();

const props = defineProps<{
  savedReport: SavedReport;
}>();

const { t } = useI18n();
const { activeFilters } = useActiveFilter();
const { notify, TOAST_TYPES } = useAToast();
const { reset } = useADialog();
const { getChanges, getSnapshot, isSavedReportGoneError, isSavedReportsOffError, removeSavedReport } =
  useSavedReports();

const CHANGES = getChanges();
const CHANGED_FILTER_LABELS = (CHANGES?.filters ?? []).map((name) => {
  const filter = activeFilters.value.find((activeFilter) => activeFilter.name === name);

  return t(filter?.text?.label || name);
});
const TOAST_ERROR = { title: 'filters.savedReports.errorToast', type: TOAST_TYPES.ERROR };
const TOAST_FEATURE_OFF = { title: 'filters.savedReports.featureOffToast', type: TOAST_TYPES.WARNING };
const TOAST_GONE = { title: 'filters.savedReports.goneToast', type: TOAST_TYPES.WARNING };
const TOAST_SUCCESS = { title: 'filters.savedReports.updatedToast', type: TOAST_TYPES.SUCCESS };

const loading = ref(false);

// EVENTS
function onUpdate() {
  if (loading.value) return;

  loading.value = true;

  const PAYLOAD = {
    ...getSnapshot(),
    _id: props.savedReport._id,
    name: props.savedReport.name,
  };

  UpdateSavedReportService(PAYLOAD)
    .then(onUpdateSuccess)
    .catch(onUpdateError)
    .finally(() => (loading.value = false));
}

function onUpdateSuccess({ data }: ApiResponse<SavedReport>) {
  emit('onUpdated', data);

  notify(TOAST_SUCCESS);

  reset();
}

// NOTE :: A 404 means the open report was deleted elsewhere: drop its row, then close it the same way Clear does.
function onUpdateError(error: any) {
  if (isSavedReportGoneError(error)) return onReportGone();

  notify(isSavedReportsOffError(error) ? TOAST_FEATURE_OFF : TOAST_ERROR);
}

function onReportGone() {
  removeSavedReport(props.savedReport._id);

  notify(TOAST_GONE);

  emit('onClear');
}
</script>

<template>
  <ADialogWrapper unstyledFooter>
    <template #header>{{ $t('filters.savedReports.updateHeader') }}</template>

    <p class="text-sm text-slate-600">
      {{ $t('filters.savedReports.updateMessage', { name: savedReport.name }) }}
    </p>

    <ul class="mt-3 flex list-disc flex-col gap-1 pl-5 text-sm text-slate-600">
      <li v-if="CHANGED_FILTER_LABELS.length">
        {{ $t('filters.savedReports.changedFilters', { filters: CHANGED_FILTER_LABELS.join(', ') }) }}
      </li>

      <li v-if="CHANGES?.selectFilter">{{ $t('filters.savedReports.changedSelectFilter') }}</li>

      <li v-if="CHANGES?.columns">{{ $t('filters.savedReports.changedColumns') }}</li>
    </ul>

    <template #footer>
      <div class="flex w-full flex-wrap justify-between gap-3">
        <Button
          class="af-button"
          icon="pi pi-eraser"
          severity="secondary"
          :disabled="loading"
          :label="$t('filters.clear')"
          @click="emit('onClear')"
        />

        <div class="flex flex-wrap gap-3">
          <Button
            outlined
            class="af-button"
            icon="pi pi-copy"
            :disabled="loading"
            :label="$t('filters.savedReports.saveAsNew')"
            @click="emit('onSaveAsNew')"
          />

          <Button
            class="af-button af-button-success"
            icon="pi pi-refresh"
            :label="$t('filters.savedReports.update')"
            :loading
            @click="onUpdate"
          />
        </div>
      </div>
    </template>
  </ADialogWrapper>
</template>
